import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import api from '@/api'
import { useAuthStore } from '@/features/account/session'
import type { MyProfileResponse } from '@/types/api'

export const useMyProfileStore = defineStore('myProfile', () => {
  const auth = useAuthStore()
  const profile = ref<MyProfileResponse | null>(null)
  const accountId = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastFetched = ref<number | null>(null)
  let version = 0
  let pending: Promise<MyProfileResponse | null> | null = null

  function setAccount(id: string | null) {
    if (accountId.value === id) return
    version++
    accountId.value = id
    profile.value = null
    lastFetched.value = null
    loading.value = false
    error.value = null
    pending = null
  }

  function fetchMyProfile(force = false): Promise<MyProfileResponse | null> {
    setAccount(auth.userId)
    const id = accountId.value
    if (!id) return Promise.resolve(null)
    if (!force && pending) return pending
    if (!force && lastFetched.value != null && Date.now() - lastFetched.value < 5 * 60 * 1000) return Promise.resolve(profile.value)
    const request = ++version
    const current = () => request === version && id === auth.userId
    loading.value = true
    error.value = null
    const result = Promise.resolve().then(async () => {
      if (!current()) return null
      try {
        const saved = await api.getMyProfile()
        if (!current()) return null
        if (!saved || saved.id !== id) throw new Error('Your profile information is unavailable. Please retry.')
        profile.value = saved
        lastFetched.value = Date.now()
        return saved
      } catch (cause) {
        if (!current()) return null
        error.value = cause instanceof Error ? cause.message : 'Could not load your profile.'
        throw cause
      } finally {
        if (current()) { loading.value = false; pending = null }
      }
    })
    pending = result
    return result
  }

  function setProfile(saved: MyProfileResponse) {
    setAccount(auth.userId)
    if (!accountId.value || saved.id !== accountId.value) return
    version++
    pending = null
    profile.value = saved
    lastFetched.value = Date.now()
    loading.value = false
    error.value = null
  }

  return {
    profile, accountId, loading, error, lastFetched, setAccount, fetchMyProfile, setProfile,
    username: computed(() => profile.value?.username || (import.meta.env.VITE_DEV_MODE === 'true' ? import.meta.env.VITE_DEV_USERNAME : '') || 'User'),
    avatarUrl: computed(() => profile.value?.avatar_url ?? ''),
    avatarFocus: computed(() => profile.value?.avatar_focus ?? null),
  }
})
